import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils.js';
import { Checkbox } from '../ui/checkbox.js';
import { DataTableColumnHeader } from './data-table-column-header.js';

/**
 * Column builders for the shapes every table in this platform repeats.
 *
 * They exist so "a date column" looks the same in every product — right-aligned,
 * tabular figures, one locale format — instead of each table re-deciding.
 */

interface BaseOptions {
  id: string;
  header: string;
  enableSorting?: boolean;
  enableHiding?: boolean;
}

/** Leading checkbox column for row selection. */
export function selectionColumn<TData>(): ColumnDef<TData, unknown> {
  return {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all rows on this page"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        // Stop the row's own onClick from firing when the checkbox is hit.
        onClick={(event) => event.stopPropagation()}
      />
    ),
    enableSorting: false,
    enableHiding: false,
    size: 32,
  };
}

export function textColumn<TData>(
  accessor: (row: TData) => string,
  { id, header, enableSorting = true, enableHiding = true }: BaseOptions,
): ColumnDef<TData, string> {
  return {
    id,
    accessorFn: accessor,
    header: ({ column }) => <DataTableColumnHeader column={column} title={header} />,
    cell: ({ getValue }) => <span className="truncate">{getValue()}</span>,
    enableSorting,
    enableHiding,
  };
}

/** Monospaced identifier column — ids and reference numbers read better in a mono face. */
export function idColumn<TData>(
  accessor: (row: TData) => string,
  options: BaseOptions,
): ColumnDef<TData, string> {
  return {
    ...textColumn(accessor, options),
    cell: ({ getValue }) => (
      <span className="font-mono text-[12.5px] text-muted-foreground">{getValue()}</span>
    ),
  };
}

export function dateColumn<TData>(
  accessor: (row: TData) => string | Date | null | undefined,
  {
    id,
    header,
    enableSorting = true,
    enableHiding = true,
    locale,
    format = { dateStyle: 'medium' },
  }: BaseOptions & { locale?: string; format?: Intl.DateTimeFormatOptions },
): ColumnDef<TData, string | Date | null | undefined> {
  const formatter = new Intl.DateTimeFormat(locale, format);
  return {
    id,
    accessorFn: accessor,
    header: ({ column }) => <DataTableColumnHeader column={column} title={header} />,
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return <span className="text-muted-foreground">—</span>;
      const date = value instanceof Date ? value : new Date(value);
      return (
        <time dateTime={date.toISOString()} className="tabular-nums">
          {formatter.format(date)}
        </time>
      );
    },
    enableSorting,
    enableHiding,
  };
}

export function currencyColumn<TData>(
  accessor: (row: TData) => number | null | undefined,
  {
    id,
    header,
    enableSorting = true,
    enableHiding = true,
    currency = 'USD',
    locale,
  }: BaseOptions & { currency?: string; locale?: string },
): ColumnDef<TData, number | null | undefined> {
  return {
    id,
    accessorFn: accessor,
    header: ({ column }) => <DataTableColumnHeader column={column} title={header} align="right" />,
    cell: ({ getValue }) => {
      const value = getValue();
      return (
        // Numbers align right with tabular figures so magnitudes are comparable down the
        // column — the single most useful thing a money column can do.
        <span
          className={cn('block text-right tabular-nums', value == null && 'text-muted-foreground')}
        >
          {value == null
            ? '—'
            : new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value)}
        </span>
      );
    },
    enableSorting,
    enableHiding,
  };
}

/** Column rendering an arbitrary node — status pills, avatars, link cells. */
export function customColumn<TData>(
  render: (row: TData) => ReactNode,
  { id, header, enableSorting = false, enableHiding = true }: BaseOptions,
): ColumnDef<TData, unknown> {
  return {
    id,
    header: ({ column }) =>
      enableSorting ? (
        <DataTableColumnHeader column={column} title={header} />
      ) : (
        <span>{header}</span>
      ),
    cell: ({ row }) => render(row.original),
    enableSorting,
    enableHiding,
  };
}
